import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, BookOpen, User, Mail, Home, FileText, MessageSquare, Globe, Activity, Music, Sparkles, MapPin, Megaphone, Images } from "lucide-react";
import api, { CATEGORIES, categoryLabel, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [statusFilter, setStatusFilter] = useState("published");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await api.get("/admin/endorsements/pending");
      setPendingReviews((data || []).length);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this writing? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Writing deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-dashboard">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-10">
        <a
          href="https://app.emergent.sh/chat"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-emergent-link"
          className="inline-flex items-center gap-3 px-5 py-3 border border-terracotta bg-paper text-terracotta text-xs tracking-[0.2em] uppercase hover:bg-terracotta hover:text-paper transition-colors"
        >
          <Sparkles size={14} />
          Edit site with Emergent
          <span className="text-[10px] tracking-[0.15em] opacity-70 normal-case">
            (opens in a new tab)
          </span>
        </a>
      </section>
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 pb-10">
        <div className="flex items-end justify-between border-b border-rule pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
              Writing Desk
            </p>
            <h1 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink">
              Your Writings
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => navigate("/admin/homepage")}
              data-testid="admin-homepage-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <Home size={16} /> Home Page
            </button>
            <button
              onClick={() => navigate("/admin/announcement")}
              data-testid="admin-announcement-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-terracotta text-terracotta text-xs tracking-[0.18em] uppercase hover:bg-terracotta hover:text-paper transition-colors"
            >
              <Megaphone size={16} /> Announcement
            </button>
            <button
              onClick={() => navigate("/admin/pages")}
              data-testid="admin-pages-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <FileText size={16} /> Site Copy
            </button>
            <button
              onClick={() => navigate("/admin/missions")}
              data-testid="admin-missions-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <Globe size={16} /> Missions
            </button>
            <button
              onClick={() => navigate("/admin/reviews")}
              data-testid="admin-reviews-btn"
              className="relative inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <MessageSquare size={16} /> Reviews
              {pendingReviews > 0 && (
                <span
                  data-testid="admin-reviews-badge"
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-terracotta text-paper text-[10px] tracking-normal rounded-full"
                >
                  {pendingReviews}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate("/admin/profile")}
              data-testid="admin-profile-edit-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <User size={16} /> Profile
            </button>
            <button
              onClick={() => navigate("/admin/book")}
              data-testid="admin-book-edit-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <BookOpen size={16} /> Book Details
            </button>
            <button
              onClick={() => navigate("/admin/subscribers")}
              data-testid="admin-subscribers-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <Mail size={16} /> Subscribers
            </button>
            <button
              onClick={() => navigate("/admin/analytics")}
              data-testid="admin-analytics-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <Activity size={16} /> Visitors
            </button>
            <button
              onClick={() => navigate("/admin/songs")}
              data-testid="admin-songs-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <Music size={16} /> Songs
            </button>
            <button
              onClick={() => navigate("/admin/journey")}
              data-testid="admin-journey-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <MapPin size={16} /> Lucy's Journey
            </button>
            <button
              onClick={() => navigate("/admin/slides")}
              data-testid="admin-slides-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              <Images size={16} /> Slideshow
            </button>
            <button
              onClick={() => navigate("/admin/new")}
              data-testid="admin-new-post-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors"
            >
              <Plus size={16} /> New Writing
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20">
        {/* Status tabs: Published / Drafts / Notes / Archive */}
        {!loading && posts.length > 0 && (
          <div
            data-testid="admin-status-tabs"
            className="flex flex-wrap gap-1 mb-6 border-b border-rule"
          >
            {[
              { v: "published", label: "Published" },
              { v: "draft", label: "Drafts" },
              { v: "notes", label: "Notes" },
              { v: "archive", label: "Archive" },
            ].map((t) => {
              const count = posts.filter((p) => (p.status || "draft") === t.v).length;
              const active = statusFilter === t.v;
              return (
                <button
                  key={t.v}
                  onClick={() => setStatusFilter(t.v)}
                  data-testid={`admin-tab-${t.v}`}
                  className={`px-4 py-2.5 text-xs tracking-[0.18em] uppercase border-b-2 -mb-px transition-colors ${
                    active
                      ? "border-terracotta text-ink"
                      : "border-transparent text-ink-soft hover:text-ink"
                  }`}
                >
                  {t.label}{" "}
                  <span className="text-ink-soft tabular-nums ml-1">
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <p className="text-ink-soft" data-testid="admin-loading">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="py-20 text-center" data-testid="admin-empty">
            <p className="font-serif-display text-2xl italic text-ink-soft">
              No writings yet — start with your first piece.
            </p>
            <button
              onClick={() => navigate("/admin/new")}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-ink text-paper text-xs tracking-[0.18em] uppercase"
              data-testid="admin-empty-cta"
            >
              <Plus size={16} /> Begin a Writing
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-rule" data-testid="admin-posts-list">
            {posts
              .filter((p) => (p.status || "draft") === statusFilter)
              .map((p, i) => (
              <li
                key={p.id}
                data-testid={`admin-post-row-${i}`}
                className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
              >
                <div className="md:col-span-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-sage mb-2">
                    {categoryLabel(p.category)}
                  </p>
                  <p className="font-serif-display text-2xl text-ink leading-tight">
                    {p.title || <span className="italic text-ink-soft">Untitled</span>}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <span
                    className={`text-[11px] uppercase tracking-[0.18em] px-2 py-1 border ${
                      p.status === "published"
                        ? "border-sage text-sage"
                        : p.status === "notes"
                        ? "border-terracotta text-terracotta"
                        : p.status === "archive"
                        ? "border-ink-soft text-ink-soft"
                        : "border-rule text-ink-soft"
                    }`}
                    data-testid={`admin-status-${i}`}
                  >
                    {p.status}
                  </span>
                </div>
                <div className="md:col-span-2 text-xs text-ink-soft">
                  {new Date(p.updated_at).toLocaleDateString()}
                </div>
                <div className="md:col-span-2 flex items-center gap-3 md:justify-end">
                  {p.status === "published" && (
                    <Link
                      to={`/writings/${p.slug}`}
                      title="View"
                      data-testid={`admin-view-${i}`}
                      className="text-ink-soft hover:text-ink"
                    >
                      <Eye size={18} />
                    </Link>
                  )}
                  <Link
                    to={`/admin/edit/${p.id}`}
                    title="Edit"
                    data-testid={`admin-edit-${i}`}
                    className="text-ink-soft hover:text-terracotta"
                  >
                    <Pencil size={18} />
                  </Link>
                  <button
                    onClick={() => handleDelete(p.id)}
                    title="Delete"
                    data-testid={`admin-delete-${i}`}
                    className="text-ink-soft hover:text-terracotta"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export { CATEGORIES };
